import { useState } from "react";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { fmtInvoiceDate } from "./OnlineInvoiceDetailModal";
import {
  LinkSimple, PaperPlaneTilt, Eye, CheckCircle, XCircle, BellRinging, ArrowCounterClockwise,
  ChatCircleText, EnvelopeSimple, CaretDown,
} from "@phosphor-icons/react";

const VIA = { sms: "SMS", email: "email", both: "SMS & email" };
const EVENTS = {
  created: { icon: LinkSimple, tone: "text-[#1E5FEB] bg-[#DBEAFE]", label: (e) => `Link created for $${money(e.amount)}` },
  sent: { icon: PaperPlaneTilt, tone: "text-[#1E5FEB] bg-[#DBEAFE]", label: (e) => `Sent by ${VIA[e.via] || "message"}` },
  opened: { icon: Eye, tone: "text-sky-700 bg-sky-100", label: (e) => e.note ? "Customer opened the invoice page" : "Customer opened the link" },
  reminder: { icon: BellRinging, tone: "text-amber-700 bg-amber-100", label: (e) => `Reminder sent by ${VIA[e.via] || "message"}` },
  paid: { icon: CheckCircle, tone: "text-emerald-700 bg-emerald-100", label: (e) => `Paid $${money(e.amount)} online` },
  cancelled: { icon: XCircle, tone: "text-slate-600 bg-slate-100", label: () => "Link cancelled" },
  refunded: { icon: ArrowCounterClockwise, tone: "text-rose-700 bg-rose-100", label: (e) => `Refunded $${money(e.amount)} to card` },
};
const STATUS = {
  active: { text: "Link active", cls: "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E5FEB]" },
  paid: { text: "Paid online", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  cancelled: { text: "Link cancelled", cls: "bg-slate-50 border-slate-200 text-slate-500" },
};

function TimelineRow({ e, last }) {
  const cfg = EVENTS[e.type] || EVENTS.created;
  const Icon = cfg.icon;
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0" data-testid={`link-event-${e.type}`}>
      {!last && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200" />}
      <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${cfg.tone}`}>
        <Icon size={14} weight="bold" />
      </span>
      <div className="min-w-0 pt-0.5">
        <div className="text-[13px] font-semibold text-[#121216]">{cfg.label(e)}</div>
        <div className="text-[11px] text-slate-500">
          {fmtInvoiceDate(e.at)}{e.by ? ` · ${e.by}` : ""}{e.type === "cancelled" && e.note ? ` · ${e.note}` : ""}
        </div>
      </div>
    </li>
  );
}

function ReminderButton({ invoice, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async (via) => {
    setOpen(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/online-invoices/${invoice.id}/stripe-link/remind`, { send_via: via });
      if (data.ok) toast.success(`Reminder sent by ${VIA[via]}`);
      (data.notification?.errors || []).forEach((m) => toast.error(m));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} disabled={busy} data-testid="link-remind-btn"
              className="h-8 px-3 rounded-xl border border-slate-200 bg-white text-[12px] font-semibold text-[#1E5FEB] hover:border-[#BFDBFE] hover:bg-[#F5F9FF] flex items-center gap-1.5 disabled:opacity-50 transition-[background-color,border-color]">
        <BellRinging size={14} weight="bold" /> {busy ? "Sending…" : "Send reminder"} <CaretDown size={10} weight="bold" />
      </button>
      {open && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
      {open && (
        <div className="absolute right-0 top-9 z-20 w-40 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          {invoice.customer_phone && (
            <button onClick={() => send("sms")} data-testid="link-remind-sms" className="w-full flex items-center gap-2 px-3 py-2.5 text-[12.5px] font-semibold text-slate-800 hover:bg-slate-50">
              <ChatCircleText size={14} weight="bold" className="text-[#1E5FEB]" /> By SMS
            </button>
          )}
          {invoice.customer_email && (
            <button onClick={() => send("email")} data-testid="link-remind-email" className="w-full flex items-center gap-2 px-3 py-2.5 text-[12.5px] font-semibold text-slate-800 hover:bg-slate-50">
              <EnvelopeSimple size={14} weight="bold" className="text-[#1E5FEB]" /> By email
            </button>
          )}
          {!invoice.customer_phone && !invoice.customer_email && (
            <div className="px-3 py-2.5 text-[12px] text-slate-500">No phone or email on file</div>
          )}
        </div>
      )}
    </div>
  );
}

function CancelLinkButton({ invoice, onDone }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/online-invoices/${invoice.id}/stripe-link/cancel`, { reason: "Cancelled by staff" });
      toast.success("Payment link cancelled — the customer can no longer pay it");
      setConfirm(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (!confirm) {
    return (
      <button onClick={() => setConfirm(true)} data-testid="link-cancel-btn"
              className="h-8 px-3 rounded-xl border border-slate-200 bg-white text-[12px] font-semibold text-rose-600 hover:border-rose-200 hover:bg-rose-50 flex items-center gap-1.5 transition-[background-color,border-color]">
        <XCircle size={14} weight="bold" /> Cancel link
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5" data-testid="link-cancel-confirm">
      <span className="text-[12px] font-semibold text-slate-600">Cancel this link?</span>
      <button onClick={cancel} disabled={busy} data-testid="link-cancel-yes" className="h-8 px-3 rounded-xl bg-rose-600 text-white text-[12px] font-semibold hover:bg-rose-700 disabled:opacity-50">
        {busy ? "Cancelling…" : "Yes, cancel"}
      </button>
      <button onClick={() => setConfirm(false)} data-testid="link-cancel-no" className="h-8 px-3 rounded-xl border border-slate-200 text-[12px] font-semibold text-slate-600 hover:bg-slate-50">Keep</button>
    </div>
  );
}

export function StripeLinkPanel({ invoice, onUpdated }) {
  const links = invoice.stripe_links || [];
  const events = invoice.link_events || [];
  if (!links.length) return null;
  const current = links[links.length - 1];
  const st = STATUS[current.status] || STATUS.cancelled;
  const ordered = [...events].reverse();
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)]" data-testid="stripe-link-panel">
      <div className="px-4 py-3 bg-[#FAFBFC] border-b border-[#E5E7EB] flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-8 h-8 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">
            <LinkSimple size={15} weight="bold" className="text-[#1E5FEB]" />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">Stripe payment link</div>
            <div className="text-[14px] font-bold text-[#121216] tabular-nums" data-testid="stripe-link-amount">${money(current.amount)}</div>
          </div>
          <span className={`ml-1 inline-flex items-center h-6 px-2.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${st.cls}`} data-testid="stripe-link-status">
            {st.text}
          </span>
        </div>
        {current.status === "active" && (
          <div className="flex items-center gap-2">
            <ReminderButton invoice={invoice} onDone={onUpdated} />
            <CancelLinkButton invoice={invoice} onDone={onUpdated} />
          </div>
        )}
      </div>
      {ordered.length > 0 && (
        <ol className="px-4 py-4" data-testid="stripe-link-timeline">
          {ordered.map((e, i) => <TimelineRow key={`${e.type}-${e.at}-${i}`} e={e} last={i === ordered.length - 1} />)}
        </ol>
      )}
    </div>
  );
}
